import { usePageTitle } from "@/hooks/usePageTitle";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Calculator, Timer, FileDown, Zap, Flame, ArrowRight } from "lucide-react";

const FEATURES = [
  {
    icon: Calculator,
    title: "Carb Sizing & Timing",
    text: "Clear guidelines on calculating your daily carbohydrate targets (grams per kg of body weight) based on your training volume—from light days to extreme endurance.",
  },
  {
    icon: Timer,
    title: "Peri-Workout Strategies",
    text: "Exactly what to eat before, during, and after your workouts to maximise performance and speed up recovery.",
  },
  {
    icon: FileDown,
    title: "Downloadable Cheat Sheets",
    text: "Practical, at-a-glance guides and meal ideas in the library to take the guesswork out of your training week.",
  },
];

export default function Sport() {
  usePageTitle("Sport — The Athlete Hub");
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 lg:px-8 pt-28 pb-24" data-testid="sport-page">
        {/* intro */}
        <div className="bg-navy rounded-2xl p-8 sm:p-12 text-white relative overflow-hidden">
          <div className="brand-blob bg-teal" style={{ width: 300, height: 300, top: -80, right: -60 }} />
          <div className="relative max-w-3xl">
            <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-semibold text-teal">
              <Zap className="w-4 h-4" /> High-performance nutrition
            </span>
            <h1 className="mt-3 font-heading text-4xl sm:text-5xl tracking-tight">The Athlete Hub</h1>
            <p className="mt-5 text-white/80 leading-relaxed">
              Your dedicated space for high-performance nutrition. If you are a mature female who takes your
              training seriously, you are by definition <em>&lsquo;An Athlete&rsquo;</em>. It&rsquo;s not about whether or not you
              are competitive, it&rsquo;s about the regular physical demands on your body&hellip; and hormones!
            </p>
            <p className="mt-4 text-white/70 leading-relaxed">
              A standard, generalized low-carbohydrate approach simply won&rsquo;t work; you will end up stressed,
              wired and tired. High-intensity endurance training demands smart energy pathways, and for women
              over 45 navigating changing hormones, fuelling requires a more nuanced approach.
            </p>
            <p className="mt-4 text-white/70 leading-relaxed">
              Here, we bridge the gap: teaching you how to properly fuel your hard sessions to prevent the
              dreaded &lsquo;bonk&rsquo; and support recovery, while preserving your body&rsquo;s efficiency as a natural fat burner.
            </p>
          </div>
        </div>

        {/* features */}
        <div className="mt-14">
          <h2 className="font-heading text-2xl sm:text-3xl text-navy">What You&rsquo;ll Find Inside</h2>
          <div className="mt-6 grid sm:grid-cols-3 gap-5">
            {FEATURES.map((f) => (
              <div key={f.title} className="bg-white border border-[#E7E5E4] rounded-2xl p-6 shadow-sm" data-testid={`sport-feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="w-11 h-11 rounded-xl bg-teal/10 text-teal flex items-center justify-center"><f.icon className="w-5 h-5" /></div>
                <h3 className="mt-4 font-heading text-lg text-navy">{f.title}</h3>
                <p className="mt-1.5 text-sm text-[#57534E] leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 bg-teal/10 border border-teal/20 rounded-2xl p-6 flex flex-wrap items-center justify-between gap-5">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-teal text-white flex items-center justify-center shrink-0"><Flame className="w-5 h-5" /></div>
            <p className="font-heading text-lg text-navy">Fuel the engine right, protect your biology, and let&rsquo;s hit those performance goals.</p>
          </div>
          <Link to="/join" data-testid="sport-join-cta"
            className="inline-flex items-center gap-2 bg-teal text-white hover:bg-teal-dark rounded-full px-7 py-3 text-sm font-semibold transition-colors">
            Unlock the Athlete Hub <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}
